import moment from "moment";
import { RECURRING_MEETING_MAX_SPAN_DAYS } from "../Constants/constantValues";

export const WEEKDAY_KEYS = [
  "meetings.weekday_sun",
  "meetings.weekday_mon",
  "meetings.weekday_tue",
  "meetings.weekday_wed",
  "meetings.weekday_thu",
  "meetings.weekday_fri",
  "meetings.weekday_sat",
];

export const ORDINAL_KEYS = {
  1: "meetings.ordinal_first",
  2: "meetings.ordinal_second",
  3: "meetings.ordinal_third",
  4: "meetings.ordinal_fourth",
  "-1": "meetings.ordinal_last",
};

export const createDefaultRecurrencePattern = (startDate) => ({
  frequency: "none",
  weeklyDaysOfWeek: [moment(startDate).day()],
  monthlyPatternType: "day_of_month",
  monthlyDayOfMonth: moment(startDate).date(),
  monthlyWeekOrdinal: 1,
  monthlyWeekday: moment(startDate).day(),
  endsType: "on_date",
  endsOnDate: maxEndDate(startDate),
  endsAfterOccurrences: 1,
});

export const maxEndDate = (startDate) =>
  moment(startDate).add(RECURRING_MEETING_MAX_SPAN_DAYS - 1, "days").toDate();

const monthlyOccurrenceInMonth = (pattern, monthStart) => {
  if (pattern.monthlyPatternType === "nth_weekday") {
    return nthWeekdayInMonth(monthStart, pattern.monthlyWeekday, pattern.monthlyWeekOrdinal);
  }

  const daysInMonth = moment(monthStart).daysInMonth();
  const day = pattern.monthlyDayOfMonth || 1;
  if (day > daysInMonth) return null;
  return moment(monthStart).date(day);
};

const nthWeekdayInMonth = (monthStart, weekday, ordinal) => {
  const monthEnd = moment(monthStart).endOf("month").startOf("day");

  if (ordinal === -1) {
    const date = monthEnd.clone();
    while (date.day() !== weekday) date.subtract(1, "day");
    return date;
  }

  const date = moment(monthStart).startOf("day");
  while (date.day() !== weekday) date.add(1, "day");
  date.add((ordinal - 1) * 7, "days");
  return date.isAfter(monthEnd) ? null : date;
};

export const patternDates = (pattern, fromDate, toDate) => {
  const from = moment(fromDate).startOf("day");
  const to = moment(toDate).startOf("day");
  if (to.isBefore(from)) return [];

  const dates = [];

  if (pattern.frequency === "daily") {
    const cursor = from.clone();
    while (!cursor.isAfter(to)) {
      dates.push(cursor.toDate());
      cursor.add(1, "day");
    }
  } else if (pattern.frequency === "weekly") {
    const days = pattern.weeklyDaysOfWeek || [];
    const cursor = from.clone();
    while (!cursor.isAfter(to)) {
      if (days.includes(cursor.day())) dates.push(cursor.toDate());
      cursor.add(1, "day");
    }
  } else if (pattern.frequency === "monthly") {
    const monthCursor = moment(from).startOf("month");
    while (!monthCursor.isAfter(to)) {
      const occurrence = monthlyOccurrenceInMonth(pattern, monthCursor);
      if (occurrence && !occurrence.isBefore(from) && !occurrence.isAfter(to)) {
        dates.push(occurrence.toDate());
      }
      monthCursor.add(1, "month");
    }
  }

  return dates;
};

export const maxOccurrences = (pattern, startDate) => {
  const start = moment(startDate).startOf("day");
  const cap = moment(maxEndDate(startDate)).startOf("day");
  return patternDates(pattern, start.toDate(), cap.toDate()).length;
};

export const effectiveEndsInfo = (pattern, startDate) => {
  const cap = maxEndDate(startDate);

  if (pattern.endsType === "on_date") {
    const untilDate = pattern.endsOnDate || cap;
    const occurrenceCount = patternDates(pattern, startDate, untilDate).length;
    return { untilDate, occurrenceCount };
  }

  const allDates = patternDates(pattern, startDate, cap);
  const index = Math.max((pattern.endsAfterOccurrences || 1) - 1, 0);
  const untilDate = allDates[index] || cap;
  return { untilDate, occurrenceCount: pattern.endsAfterOccurrences || 1 };
};

export const formatFrequencyPhrase = (t, pattern) => {
  if (pattern.frequency === "daily") {
    return t("meetings.repeat_every_day");
  }

  if (pattern.frequency === "weekly") {
    const days = (pattern.weeklyDaysOfWeek || [])
      .slice()
      .sort((a, b) => a - b)
      .map((day) => t(WEEKDAY_KEYS[day]))
      .join(", ");
    return t("meetings.repeat_every_week_on", { days });
  }

  if (pattern.frequency === "monthly" && pattern.monthlyPatternType === "nth_weekday") {
    return t("meetings.repeat_every_month_on_nth_weekday", {
      ordinal: t(ORDINAL_KEYS[pattern.monthlyWeekOrdinal]),
      weekday: t(WEEKDAY_KEYS[pattern.monthlyWeekday]),
    });
  }

  if (pattern.frequency === "monthly") {
    return t("meetings.repeat_every_month_on_day", { day: pattern.monthlyDayOfMonth });
  }

  return t("meetings.does_not_repeat");
};

export const formatRecurrenceSummary = (t, pattern, { untilDate, occurrenceCount }) => {
  if (pattern.frequency === "none") {
    return t("meetings.does_not_repeat");
  }

  return t("meetings.recurrence_summary_until", {
    frequency: formatFrequencyPhrase(t, pattern),
    date: moment(untilDate).format("MMM D, YYYY"),
    count: occurrenceCount,
  });
};

export const formatRecurrenceCadence = (t, seriesSummary) => {
  if (!seriesSummary) return "";

  return formatFrequencyPhrase(t, {
    frequency: seriesSummary.frequency,
    weeklyDaysOfWeek: seriesSummary.weekly_days_of_week,
    monthlyPatternType: seriesSummary.monthly_pattern_type,
    monthlyDayOfMonth: seriesSummary.monthly_day_of_month,
    monthlyWeekOrdinal: seriesSummary.monthly_week_ordinal,
    monthlyWeekday: seriesSummary.monthly_weekday,
  });
};

export const groupMeetingsByDay = (meetings) => {
  const groups = [];
  let currentKey = null;

  meetings.forEach((meeting) => {
    const key = moment(meeting.start_at).format("YYYY-MM-DD");
    if (key !== currentKey) {
      groups.push({ key, date: moment(meeting.start_at).startOf("day").toDate(), meetings: [] });
      currentKey = key;
    }
    groups[groups.length - 1].meetings.push(meeting);
  });

  return groups;
};

export const hasUnmaterializedOccurrences = (meetings) => {
  const latestIndexBySeriesId = {};
  const totalBySeriesId = {};

  meetings.forEach((meeting) => {
    const seriesId = meeting.recurring_meeting_series_id;
    if (!seriesId || !meeting.recurring_meeting_series_summary) return;

    const index = meeting.series_occurrence_index || 0;
    if (!latestIndexBySeriesId[seriesId] || index > latestIndexBySeriesId[seriesId]) {
      latestIndexBySeriesId[seriesId] = index;
    }
    totalBySeriesId[seriesId] = meeting.recurring_meeting_series_summary.total_occurrences;
  });

  return Object.keys(latestIndexBySeriesId).some(
    (seriesId) => latestIndexBySeriesId[seriesId] < totalBySeriesId[seriesId]
  );
};

export const patternToApiParams = (pattern) => {
  const params = {
    frequency: pattern.frequency,
    ends_type: pattern.endsType,
  };

  if (pattern.frequency === "weekly") {
    params.weekly_days_of_week = pattern.weeklyDaysOfWeek;
  }

  if (pattern.frequency === "monthly") {
    params.monthly_pattern_type = pattern.monthlyPatternType;
    if (pattern.monthlyPatternType === "day_of_month") {
      params.monthly_day_of_month = pattern.monthlyDayOfMonth;
    } else {
      params.monthly_week_ordinal = pattern.monthlyWeekOrdinal;
      params.monthly_weekday = pattern.monthlyWeekday;
    }
  }

  if (pattern.endsType === "on_date") {
    params.ends_on_date = moment(pattern.endsOnDate).format("YYYY-MM-DD");
  } else {
    params.ends_after_occurrences = pattern.endsAfterOccurrences;
  }

  return params;
};

export const seriesToPattern = (series) => ({
  frequency: series.frequency,
  weeklyDaysOfWeek: series.weekly_days_of_week || [],
  monthlyPatternType: series.monthly_pattern_type || "day_of_month",
  monthlyDayOfMonth: series.monthly_day_of_month,
  monthlyWeekOrdinal: series.monthly_week_ordinal,
  monthlyWeekday: series.monthly_weekday,
  endsType: series.ends_type,
  endsOnDate: series.ends_on_date ? moment(series.ends_on_date, "YYYY-MM-DD").toDate() : null,
  endsAfterOccurrences: series.ends_after_occurrences,
});

export const patternsEqual = (a, b) => {
  if (!a || !b) return a === b;
  if (a.frequency !== b.frequency) return false;
  if (a.frequency === "none") return true;

  if (a.frequency === "weekly") {
    const aDays = [...(a.weeklyDaysOfWeek || [])].sort((x, y) => x - y);
    const bDays = [...(b.weeklyDaysOfWeek || [])].sort((x, y) => x - y);
    if (aDays.length !== bDays.length || aDays.some((day, i) => day !== bDays[i])) return false;
  }

  if (a.frequency === "monthly") {
    if (a.monthlyPatternType !== b.monthlyPatternType) return false;
    if (a.monthlyPatternType === "day_of_month" && a.monthlyDayOfMonth !== b.monthlyDayOfMonth) return false;
    if (
      a.monthlyPatternType === "nth_weekday" &&
      (a.monthlyWeekOrdinal !== b.monthlyWeekOrdinal || a.monthlyWeekday !== b.monthlyWeekday)
    ) {
      return false;
    }
  }

  if (a.endsType !== b.endsType) return false;
  if (
    a.endsType === "on_date" &&
    moment(a.endsOnDate).format("YYYY-MM-DD") !== moment(b.endsOnDate).format("YYYY-MM-DD")
  ) {
    return false;
  }
  if (a.endsType === "after_occurrences" && a.endsAfterOccurrences !== b.endsAfterOccurrences) return false;

  return true;
};

export const formatDayGroupLabel = (t, date) => {
  const target = moment(date).startOf("day");
  const diffDays = target.diff(moment().startOf("day"), "days");

  if (diffDays === 0) return t("meetings.today");
  if (diffDays === 1) return t("meetings.tomorrow");
  return target.format("ddd, DD MMM");
};
